import { type ChangeEvent, type FC, useCallback, useEffect, useRef, useState } from 'react'
import { Box, InputAdornment, type SxProps, type Theme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { IconButton, TextField, type TextFieldProps, useIsMobileVariant } from '@dhl-ui/components'
import { CloseIcon, SearchIcon } from '@dhl-ui/icons'

type Props = {
  'data-testid'?: string
  onSearchTermClear?: () => void
  onSearchTermChange?: (searchTerm: string) => void
  placeholder?: string
  searchAsYouType?: boolean
  searchTerm: string | null | undefined
  size?: TextFieldProps['size']
  sx?: SxProps<Theme>
}

const styles: Record<string, SxProps<Theme>> = {
  searchBox: {
    '& .search-clear-button': {
      visibility: 'hidden',
    },
    '& .Mui-focused .search-clear-button': {
      visibility: 'visible',
    },
  },
  clearSearchInputButton: {
    height: 'auto',
  },
}

export const SearchBox: FC<Props> = ({
  'data-testid': dataTestId = 'search-input-field',
  onSearchTermClear,
  onSearchTermChange,
  placeholder,
  searchAsYouType = true,
  searchTerm,
  size,
  sx,
}) => {
  const [searchTermState, setSearchTermState] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const { t } = useTranslation()
  const isMobileVariant = useIsMobileVariant()

  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchTermState(event.target.value)
  }, [])

  const handleSearchClear = () => {
    onSearchTermClear?.()
    setSearchTermState('')
    inputRef?.current?.focus()
  }

  const handleSearch = useCallback(() => onSearchTermChange?.(searchTermState), [onSearchTermChange, searchTermState])

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch()
    }
  }

  const handlePreventDefault = (event: React.MouseEvent) => event.preventDefault()

  useEffect(() => {
    if (searchAsYouType) {
      handleSearch()
    }
  }, [handleSearch, onSearchTermChange, searchAsYouType, searchTermState])

  return (
    <Box sx={{ ...styles.searchBox, ...sx } as SxProps<Theme>}>
      <TextField
        fullWidth
        data-testid={dataTestId}
        inputRef={inputRef}
        placeholder={placeholder ?? t('components.inputs.search.searchPlaceHolderText')}
        size={size ?? (isMobileVariant ? 'large' : 'medium')}
        slotProps={{
          input: {
            startAdornment: searchAsYouType && <SearchIcon />,
            endAdornment: (
              <>
                {searchTerm && (
                  <InputAdornment
                    className="search-clear-button"
                    data-test="search-input-field-clear"
                    position="end"
                    sx={styles.clearSearchInputButton}
                  >
                    <IconButton
                      aria-label={t('components.inputs.search.clearSearchTerm')}
                      edge="end"
                      onClick={handleSearchClear}
                      onMouseDown={handlePreventDefault}
                    >
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                )}
                {!searchAsYouType && (
                  <InputAdornment
                    className="search-button"
                    data-test="search-button"
                    position="end"
                    sx={styles.clearSearchInputButton}
                  >
                    <IconButton
                      aria-label={t('components.inputs.search.searchButtonText')}
                      edge="end"
                      size="medium"
                      onClick={handleSearch}
                      onMouseDown={handlePreventDefault}
                    >
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                )}
              </>
            ),
          },
        }}
        value={searchTermState}
        onChange={handleSearchChange}
        onKeyDown={handleKeyDown}
      />
    </Box>
  )
}
